import { Button, Modal } from "@/components/basic";
import { WorkingLocationSelector } from "@/components/dashboard/WorkingLocationSelector/WorkingLocationSelector";
import { LoadingSpinner } from "@/components/util/LoadingSpinner";
import { useWorkplaceRequestTypes } from "@/hooks/useWorkplaceRequestTypes";
import { CreateWorkplaceRequestParams } from "@gettactic/api";
import Alert from "@gettactic/components/src/data-display/Alert/Alert";
import { HookFormTextInput } from "@gettactic/components/src/form/TextInput";
import { zodResolver } from "@hookform/resolvers/zod";
import { HookFormWRTypeSelect } from "@webV2/components/workplace-request/type-select";
import { Controller, useForm } from "react-hook-form";
import { OwnerSelect } from "./OwnerSelect";
import { useWorkplaceRequestMutations } from "@/hooks/useWorkplaceRequestMutations";
import {
  createWorkplaceRequestSchema,
  CreateWorkplaceRequestFormValues
} from "@/lib/validation/workplace-requests";
import { canScheduleOthers } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";

export interface CreateRequestDefaultValues {
  title?: string;
  description?: string;
  type?: string;
  location?: string;
  submitter?: string;
  status?: string;
  owner?: string;
}

interface CreateRequestModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isAdmin?: boolean;
  isModal?: boolean;
  defaultValues?: CreateRequestDefaultValues;
  onCreated?: () => void;
}

type FormValues = CreateWorkplaceRequestFormValues;

export const CreateRequestModal = ({
  isOpen,
  onOpenChange,
  isModal = true,
  defaultValues,
  onCreated
}: CreateRequestModalProps) => {
  const { createWorkplaceRequest, isLoading } = useWorkplaceRequestMutations();

  const { allElements: types } = useWorkplaceRequestTypes();

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const isAdmin = canScheduleOthers(authenticatedUser);

  const form = useForm<FormValues>({
    resolver: zodResolver(createWorkplaceRequestSchema),
    defaultValues: {
      title: defaultValues?.title || "",
      description: defaultValues?.description || "",
      type: defaultValues?.type || "",
      location: defaultValues?.location || "",
      submitter: defaultValues?.submitter || "",
      status: (defaultValues?.status as never) || "Open",
      owner: defaultValues?.owner || ""
    }
  });

  const selectedType = types.find((t) => t.id === form.watch("type"));
  const assignedUsers = selectedType?.assigned_users || [];

  const handleSubmit = async (values: FormValues) => {
    const requestData: CreateWorkplaceRequestParams = {
      title: values.title,
      description: values.description,
      type_id: values.type || "",
      office_id: values.location || "",
      status: values.status.toLowerCase() as "open" | "pending" | "closed",
      ...(isAdmin && values.owner && { owner_user_id: values.owner })
    };

    try {
      await createWorkplaceRequest(requestData);

      form.reset();
      onOpenChange(false);
      onCreated?.();
    } catch (error) {
      console.error("Failed to create workplace request:", error);
    }
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  const formContent = (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className={"flex max-w-full flex-col gap-6 text-sm"}>
        <HookFormTextInput
          form={form}
          label={"Title"}
          name={"title"}
          placeholder="Enter request title"
        />

        <HookFormTextInput
          form={form}
          label={"Description"}
          name={"description"}
          placeholder="Describe the request in detail"
        />

        <div className="flex flex-col gap-4 md:flex-row">
          <HookFormWRTypeSelect
            form={form}
            name="type"
            label="Type"
            placeholder="Select request type"
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex w-full items-center justify-between space-x-2 text-secondary [.tactic-v2_&]:text-secondary-foreground">
            <span>Location</span>
          </div>
          <Controller
            name="location"
            control={form.control}
            rules={{ required: "Location is required" }}
            render={({ field, fieldState }) => (
              <div>
                <WorkingLocationSelector
                  type="select"
                  value={field.value || ""}
                  setValue={field.onChange}
                  readonly={false}
                  officesOnly={true}
                  isV2={true}
                  defaultLocation={{
                    label: "Select Office",
                    value: "",
                    title: "Select Office"
                  }}
                />
                {fieldState.error && (
                  <div className="mt-1">
                    <Alert
                      variant="error"
                      canDismiss={false}
                      alertTitle={fieldState.error.message}
                      alertMessage=""
                      className="px-4 py-2"
                    />
                  </div>
                )}
              </div>
            )}
          />
        </div>

        {/* Owner selection - only show for admins */}
        {isAdmin && (
          <div className="space-y-1">
            <label htmlFor="owner" className="block text-sm">
              Owner
            </label>
            {!selectedType ? (
              <div className="text-gray-500 text-sm">
                Please select a request type first
              </div>
            ) : assignedUsers.length === 0 ? (
              <div className="text-gray-500 text-sm">
                No assigned users for this request type
              </div>
            ) : (
              <OwnerSelect
                variant="modal"
                form={form}
                name="owner"
                assignedUsers={assignedUsers}
                placeholder="Select owner (optional)"
              />
            )}
          </div>
        )}
      </div>

      <Modal.Footer>
        <Button
          className="w-full sm:w-auto"
          layout="squared"
          variant="white"
          font="boldGray"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          className="w-full sm:ml-3 sm:w-auto"
          layout="squared"
          variant="primary"
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? (
            <LoadingSpinner color="text-white" />
          ) : (
            <span>Create Request</span>
          )}
        </Button>
      </Modal.Footer>
    </form>
  );

  if (!isModal) return formContent;

  return (
    <Modal
      title="Create New Request"
      opened={isOpen}
      onClose={() => onOpenChange(false)}
      size="max-w-xxl"
    >
      {formContent}
    </Modal>
  );
};
